import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation } from 'react-router-dom'
import { differenceInMonths, parseISO } from 'date-fns'
import {
  Timeframe,
  ProjectSolutionDriver,
  ImpactArtifactRequest,
  OptimizeArtifactRequest,
} from '@/api/generated/models'
import { ROUTED_REPORT_MODES } from '../../../../reports/types/ReportTypes'

export const useModelParameters = (
  projectSolutionDrivers: ProjectSolutionDriver[],
  validateTimeDiff = true,
  timeframeFieldName: string = 'timeframe',
) => {
  const location = useLocation()
  const { mode } = location.state || {}
  const { register, unregister, watch, setValue, trigger, getValues } =
    useFormContext<OptimizeArtifactRequest | ImpactArtifactRequest>()
  const nameInputRef = useRef<HTMLInputElement | undefined>(undefined)
  const [dateRange, setDateRange] = useState<Timeframe>({ from: '', to: '' })

  // Watch dependent_driver_id for initialization
  const dependentDriverId = watch('dependent_driver_id')

  // Initialize dateRange when dependent_driver_id is already set (e.g., in edit mode)
  useEffect(() => {
    if (dependentDriverId && projectSolutionDrivers.length > 0) {
      const projectSolutionDriver = projectSolutionDrivers.find(
        (psd) => psd.driver?.id === dependentDriverId,
      )
      if (projectSolutionDriver?.driver) {
        const driverRange = {
          from: projectSolutionDriver.driver.start_at ?? '',
          to: projectSolutionDriver.driver.end_at ?? '',
        }
        // Only update if dateRange is empty (not already set)
        setDateRange((prev) => {
          if (!prev.from && !prev.to) {
            return driverRange
          }
          return prev
        })
      }
    }
  }, [dependentDriverId, projectSolutionDrivers])

  const handleDependentDriverChange = (value: string | number | null) => {
    if (!value) {
      setValue('dependent_driver_id', 0, { shouldValidate: true })
      return
    }

    const driverId = Number(value)
    const projectSolutionDriver = projectSolutionDrivers.find(
      (psd) => psd.driver?.id === driverId,
    )

    if (!projectSolutionDriver || !projectSolutionDriver.driver) return

    setValue('dependent_driver_id', driverId, {
      shouldValidate: true,
    })

    // Explicitly trigger validation to clear any errors
    trigger('dependent_driver_id')

    // Generate model name for new models if not already set
    if (mode !== ROUTED_REPORT_MODES.EDIT_MODEL) {
      const currentName = getValues('name')
      if (!currentName) {
        const generatedName = `${projectSolutionDriver.driver.name}_${new Date().getTime()}`
        setValue('name', generatedName)
      }
    }

    const currentIndependent = watch('independent_driver_ids') || []
    const isDuplicate = currentIndependent.some(
      (independent) => independent === driverId,
    )

    if (isDuplicate) {
      const updatedIndependent = currentIndependent.filter(
        (independent) => independent !== driverId,
      )
      setValue('independent_driver_ids', updatedIndependent)
    }

    const driverRange = {
      from: projectSolutionDriver.driver.start_at ?? '',
      to: projectSolutionDriver.driver.end_at ?? '',
    }
    setValue(timeframeFieldName as any, driverRange)
    setDateRange(driverRange)

    // Check if this is Impact or Optimize artifact by checking for 'parameters' vs 'optimize_config'
    const formValues = getValues()
    if ('parameters' in formValues) {
      // Impact artifact
      setValue(
        'parameters.model_data_frequency',
        projectSolutionDriver.driver.interval ?? '',
      )
    } else if ('optimize_config' in formValues) {
      // Optimize artifact
      const interval = projectSolutionDriver.driver.interval
      if (interval) {
        setValue('optimize_config.model_data_frequency', interval as any)
      }
    }
  }

  useEffect(() => {
    register('dependent_driver_id', {
      required: 'Outcome variable must be selected',
    })
    register(timeframeFieldName as any, {
      validate: (value) => {
        if (!value) return

        const { from, to } = value
        if (!from || !to) {
          return 'Please select a valid timeframe'
        }

        const fromDate = parseISO(String(from).slice(0, 10))
        const toDate = parseISO(String(to).slice(0, 10))
        if (
          Number.isNaN(fromDate.getTime()) ||
          Number.isNaN(toDate.getTime())
        ) {
          return 'Please select a valid timeframe'
        }

        // 24 monthly observations run start-month through end-month inclusive.
        // The last monthly date is typically the 1st of that month, so a
        // 730-day span check rejects a valid 2-year monthly series (~700 days).
        const inclusiveMonths = differenceInMonths(toDate, fromDate) + 1

        if (validateTimeDiff && inclusiveMonths < 24) {
          return 'To build a reliable model that captures seasonal variation, the selected timeframe must include at least 24 months (2 years) of data.'
        }

        return true
      },
    })

    return () => {
      unregister('dependent_driver_id', { keepValue: true })
      unregister(timeframeFieldName as any, { keepValue: true })
    }
  }, [register, unregister, timeframeFieldName, validateTimeDiff])

  const handleNameEditFocus = () => {
    if (nameInputRef.current) {
      nameInputRef.current.focus()
    }
  }

  return {
    dateRange,
    handleDependentDriverChange,
    nameInputRef,
    handleNameEditFocus,
  }
}
